// ============================================================
// Alpine: shopPayment — bayar Duitku POP / upload bukti transfer
// + shopPaymentResult — status setelah return Duitku
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { requireAuth, shopApi } from '../../helpers/shopAuth.js';

export function registerShopPayment() {
  Alpine.data('shopPayment', (orderId) => ({
    orderId,
    order: null,
    pending: null,
    loading: true,
    uploading: false,
    paying: false,
    proofFile: null,
    bankName: '',
    accountName: '',
    error: '',

    init() {
      if (!requireAuth(`/shop/payment/${this.orderId}`)) return;

      try {
        this.pending = JSON.parse(sessionStorage.getItem('uspatih_pending_order') || 'null');
      } catch {
        this.pending = null;
      }

      this.fetchOrder();
    },

    formatRp(n) {
      return window.formatRupiah ? window.formatRupiah(n) : `Rp ${Number(n).toLocaleString('id-ID')}`;
    },

    async fetchOrder() {
      this.loading = true;
      try {
        const { data } = await axios.get(shopApi(`/checkout/orders/${this.orderId}`));
        this.order = data.data;
      } catch (e) {
        if (this.pending?.order_id === this.orderId) {
          this.order = {
            id: this.pending.order_id,
            order_number: this.pending.order_number,
            total: this.pending.grand_total,
            payment_method: this.pending.payment?.payment_method || this.pending.payment?.payment_gateway || 'transfer_manual',
            payment_status: this.pending.payment_status,
            invoice: this.pending.invoice,
          };
        } else {
          this.error = e.response?.data?.message || 'Pesanan tidak ditemukan.';
        }
      } finally {
        this.loading = false;
      }
    },

    get isDuitku() {
      const method = this.order?.payment_method || this.pending?.payment?.payment_method;
      const gateway = this.pending?.payment?.payment_gateway;
      return method === 'duitku' || method === 'midtrans' || gateway === 'duitku';
    },

    get isTransferManual() {
      if (this.isDuitku) return false;
      return this.order?.payment_method === 'transfer_manual' || this.order?.can_upload_proof || !this.isDuitku;
    },

    get reference() {
      return this.pending?.payment?.reference || null;
    },

    get paymentUrl() {
      return this.pending?.payment?.payment_url || null;
    },

    get bankAccount() {
      return this.pending?.payment?.bank_account || null;
    },

    async ensureReference() {
      if (this.reference) return this.reference;

      const { data } = await axios.post(shopApi('/payment/duitku/invoice'), {
        order_id: this.orderId,
      });
      const payment = data.data || {};
      if (this.pending) {
        this.pending.payment = { ...(this.pending.payment || {}), ...payment };
        sessionStorage.setItem('uspatih_pending_order', JSON.stringify(this.pending));
      } else {
        this.pending = {
          order_id: this.orderId,
          order_number: this.order?.order_number,
          payment,
        };
        sessionStorage.setItem('uspatih_pending_order', JSON.stringify(this.pending));
      }
      return payment.reference || null;
    },

    redirectDuitku() {
      const url = this.paymentUrl;
      if (!url) {
        window.toast?.('URL pembayaran belum tersedia. Coba Bayar dengan Duitku.', 'error');
        return;
      }
      window.location.href = url.includes('lang=') ? url : `${url}${url.includes('?') ? '&' : '?'}lang=id`;
    },

    goSuccess(orderNumber, status) {
      const q = new URLSearchParams();
      if (orderNumber) q.set('order', orderNumber);
      if (status) q.set('status', status);
      window.location.href = `/shop/success?${q.toString()}`;
    },

    async waitForPaidStatus(maxAttempts = 6) {
      for (let i = 0; i < maxAttempts; i++) {
        try {
          const { data } = await axios.get(shopApi(`/checkout/orders/${this.orderId}`));
          const ps = data.data?.payment_status;
          if (ps === 'paid' || ps === 'lunas') return 'paid';
          if (['failed', 'expired', 'cancelled'].includes(ps)) return 'failed';
        } catch {
          // ignore — callback may still be in flight
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
      return 'pending';
    },

    async payDuitku() {
      this.paying = true;
      this.error = '';
      try {
        if (this.pending?.payment?.mode === 'unconfigured' || this.pending?.payment?.error) {
          const msg = 'Pembayaran sementara tidak tersedia. Silakan coba lagi atau hubungi admin.';
          this.error = msg;
          window.toast?.(msg, 'error');
          return;
        }

        const reference = await this.ensureReference();
        if (!reference) {
          if (this.paymentUrl) {
            this.redirectDuitku();
            return;
          }
          throw new Error('Referensi pembayaran Duitku tidak tersedia.');
        }

        if (typeof window.checkout?.process !== 'function') {
          if (this.paymentUrl) {
            this.redirectDuitku();
            return;
          }
          window.toast?.('Script Duitku belum siap. Refresh halaman.', 'error');
          return;
        }

        const orderNumber = this.order?.order_number || this.pending?.order_number || '';

        window.checkout.process(reference, {
          successEvent: async () => {
            sessionStorage.removeItem('uspatih_pending_order');
            const status = await this.waitForPaidStatus();
            this.goSuccess(orderNumber, status);
          },
          pendingEvent: () => {
            this.goSuccess(orderNumber, 'pending');
          },
          errorEvent: () => {
            this.goSuccess(orderNumber, 'failed');
          },
          closeEvent: () => {
            // user menutup popup
          },
        });
      } catch (e) {
        this.error = e.response?.data?.message || e.message || 'Gagal membuka pembayaran Duitku.';
        window.toast?.(this.error, 'error');
      } finally {
        this.paying = false;
      }
    },

    onProofSelected(event) {
      this.proofFile = event.target.files?.[0] || null;
    },

    async uploadProof() {
      if (!this.proofFile) {
        window.toast?.('Pilih file bukti transfer.', 'error');
        return;
      }

      this.uploading = true;
      const form = new FormData();
      form.append('payment_proof', this.proofFile);
      if (this.bankName) form.append('bank_name', this.bankName);
      if (this.accountName) form.append('account_name', this.accountName);

      try {
        await axios.post(shopApi(`/checkout/${this.orderId}/payment-proof`), form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        sessionStorage.removeItem('uspatih_pending_order');
        this.goSuccess(this.order?.order_number || '', 'pending');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Upload gagal.', 'error');
      } finally {
        this.uploading = false;
      }
    },
  }));

  Alpine.data('shopPaymentResult', (opts = {}) => ({
    orderNumber: opts.orderNumber || null,
    orderId: opts.orderId || null,
    uiStatus: opts.initialStatus || 'pending',
    polling: false,
    pollCount: 0,
    _timer: null,

    init() {
      if (this.uiStatus !== 'pending' || !this.orderNumber) return;
      this.polling = true;
      this._timer = setInterval(() => this.pollStatus(), 3000);
      this.pollStatus();
    },

    async pollStatus() {
      this.pollCount += 1;
      if (this.pollCount > 40) {
        this.stopPolling();
        return;
      }
      try {
        const { data } = await axios.get(`/shop/payment/status`, {
          params: { order: this.orderNumber },
        });
        const next = data?.data?.ui_status;
        if (next && next !== this.uiStatus) {
          this.uiStatus = next;
          if (next !== 'pending') this.stopPolling();
        }
      } catch {
        // keep pending UI
      }
    },

    stopPolling() {
      this.polling = false;
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    },

    destroy() {
      this.stopPolling();
    },
  }));
}
